import React, {useCallback, useEffect, useRef, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {ReactComponent as HelperIcon} from 'assets/svg/helper.svg';
import {ReactComponent as ManualEnterIcon} from 'assets/svg/manual-enter.svg';
import {ReactComponent as PlusIcon} from 'assets/svg/plus.svg';
import {ReactComponent as ScanLargeIcon} from 'assets/svg/scan-large.svg';

import {Button} from 'components/ui-kit/Button';
import {Icon} from 'components/ui-kit/Icon';
import {Input} from 'components/ui-kit/Input';
import {Modal} from 'components/ui-kit/Modal';

import {colors} from 'theme';

import {Spinner} from '../Spinner';

import {Switch} from '../Switcher';

import {ScanChoose, ScanContainer, ScanReadyMessage, ScanWarn} from './styled';

type Props = {
    open: boolean;
    pending?: boolean;
    onClose: () => void;
    onScan: (code: string) => Promise<void> | void;
    onManual?: (code: string) => Promise<void> | void;
    allowToGroupByGtin?: boolean;
    combineByGtin?: boolean;
    setCombineByGtin?: (value: boolean) => void;
};

export const QRScannerModal: React.FC<Props> = ({
    open,
    pending,
    onClose,
    onScan,
    onManual,
    allowToGroupByGtin = false,
    combineByGtin = false,
    setCombineByGtin,
}) => {
    const {t} = useTranslation();
    const modalRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const [inputValue, setInputValue] = useState('');
    const [mode, setMode] = useState<'scan' | 'manual'>('scan');
    const bufferRef = useRef('');
    const timeoutRef = useRef<number | null>(null);

    const onEnter = async () => {
        if (!inputValue.trim()) return;

        await onManual?.(inputValue.trim());
        setInputValue('');
        inputRef.current?.focus();
    };

    const handleKeyDown = useCallback(
        async (e: KeyboardEvent) => {
            if (
                pending ||
                (e.key !== 'Enter' && e.key.length > 1) ||
                e.ctrlKey ||
                e.altKey ||
                e.metaKey
            )
                return;

            if (e.key === 'Enter') {
                const code = bufferRef.current.trim();
                if (code.length) {
                    await onScan(code);
                    bufferRef.current = '';
                }
                return;
            }

            bufferRef.current += e.key;

            if (timeoutRef.current) clearTimeout(timeoutRef.current);

            timeoutRef.current = window.setTimeout(() => {
                bufferRef.current = '';
            }, 500);
        },
        [open, pending],
    );

    useEffect(() => {
        if (open && mode === 'manual') {
            inputRef.current?.focus();
        } else if (open) {
            modalRef.current?.focus();
        }
    }, [open, mode]);

    useEffect(() => {
        if (!open) return;

        window.addEventListener('keydown', handleKeyDown);
        setTimeout(() => modalRef.current?.focus(), 500);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            bufferRef.current = '';
        };
    }, [open, handleKeyDown]);

    return (
        <Modal show={open} onClose={onClose} title={t('components.scan.title')} modalWidth={0}>
            <ScanContainer ref={modalRef} tabIndex={-1}>
                <span>{t('components.scan.description')}</span>
                <>
                    <ScanChoose>
                        {pending && mode === 'scan' ? (
                            <Spinner size={4} />
                        ) : (
                            <Icon
                                className={mode === 'scan' ? 'selected' : ''}
                                source={ScanLargeIcon}
                                width={70}
                                height={70}
                                onClick={() => setMode('scan')}
                            />
                        )}
                        or
                        {pending && mode === 'manual' ? (
                            <Spinner size={4} />
                        ) : (
                            <Icon
                                className={mode === 'manual' ? 'selected' : ''}
                                source={ManualEnterIcon}
                                width={74}
                                height={74}
                                fillColor="#fff"
                                onClick={() => setMode('manual')}
                            />
                        )}
                    </ScanChoose>
                    {allowToGroupByGtin && setCombineByGtin && (
                        <div>
                            <Switch
                                value={combineByGtin}
                                label="Combine products by GTIN into 1 line"
                                onChange={() => setCombineByGtin(!combineByGtin)}
                            />
                        </div>
                    )}
                </>

                {mode === 'manual' && (
                    <div>
                        <Input
                            tabIndex={-1}
                            ref={inputRef}
                            disabled={pending}
                            label={t('components.scan.label')}
                            placeholder={t('components.scan.placeholder')}
                            value={inputValue}
                            onKeyDown={(e) => e.key === 'Enter' && onEnter()}
                            onChange={(e) => setInputValue(e.target.value.replaceAll(' ', ''))}
                        />
                        <Button disabled={!inputValue.trim() || pending} onClick={onEnter}>
                            Add <Icon source={PlusIcon} width={12} height={12} />
                        </Button>
                    </div>
                )}
                {mode === 'scan' && (
                    <>
                        <ScanReadyMessage>{t('components.scan.ready')}</ScanReadyMessage>
                        <ScanWarn>
                            <Icon
                                source={HelperIcon}
                                style={{color: colors.brandPrimary}}
                                fillColor="transparent"
                                width={24}
                            />
                            <span>{t('components.scan.layoutWarn')}</span>
                        </ScanWarn>
                    </>
                )}
            </ScanContainer>
        </Modal>
    );
};
